import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { LocateFixed, Loader2, MapPin, Phone, Navigation, Store, Globe } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";

const RADII = [2, 5, 10, 20];

function FitStores({ center, stores }) {
  const map = useMap();
  useEffect(() => {
    const pts = [center, ...stores.map((s) => [s.lat, s.lng])];
    if (pts.length > 1) map.fitBounds(pts, { padding: [24, 24], maxZoom: 15 });
  }, [map, center, stores]);
  return null;
}

function StoreCard({ s }) {
  const route = `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`;
  return (
    <div className="rounded-xl border border-white/10 p-4 transition-colors hover:border-[#FFC700]/50" data-testid="nearby-store-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-bold" data-testid="nearby-store-name">{s.name}</div>
          <div className="text-xs uppercase tracking-wide text-[#FFC700]">{s.category}</div>
        </div>
        <span className="shrink-0 rounded-full bg-white/5 px-2.5 py-1 font-mono text-xs font-bold" data-testid="nearby-store-distance">{s.distance_km} km</span>
      </div>
      {(s.street || s.city) && <div className="mt-2 flex gap-1.5 text-sm text-slate-400"><MapPin size={14} className="mt-0.5 shrink-0" />{[s.street, s.district, s.city].filter(Boolean).join(" · ")}</div>}
      {s.hours && <div className="mt-1 text-xs text-slate-500">{s.hours}</div>}
      <div className="mt-3 flex flex-wrap gap-2 text-sm">
        <a href={route} target="_blank" rel="noreferrer" className="btn-ghost px-3 py-1.5 text-xs" data-testid="nearby-store-route"><Navigation size={14} />Rota</a>
        {s.phone && <a href={`tel:${s.phone.split(";")[0].replace(/\s/g, "")}`} className="btn-ghost px-3 py-1.5 text-xs" data-testid="nearby-store-phone"><Phone size={14} />Ligar</a>}
        {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="btn-ghost px-3 py-1.5 text-xs"><Globe size={14} />Site</a>}
      </div>
    </div>
  );
}

export default function NearbyStores({ title = "Lojas de materiais perto de você", limit }) {
  const api = useApi();
  const [radius, setRadius] = useState(5);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async (r, coords) => {
    setBusy(true); setErr("");
    try { const { data: d } = await api.get("/stores/nearby", { params: { radius_km: r, ...(coords || {}) } }); setData({ ...d, coords }); }
    catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };
  const locate = (r = radius) => {
    if (!navigator.geolocation) return load(r);
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => load(r, { lat: p.coords.latitude, lng: p.coords.longitude }),
      () => load(r), { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 });
  };
  const pickRadius = (r) => { setRadius(r); if (data) load(r, data.coords); };
  const list = data ? (limit ? data.stores.slice(0, limit) : data.stores) : [];

  return (
    <div className="panel p-5 sm:p-6" data-testid="nearby-stores">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-xl font-bold uppercase"><Store size={18} className="text-[#FFC700]" />{title}</h3>
        <div className="flex gap-1.5" data-testid="nearby-radius">
          {RADII.map((r) => <button key={r} type="button" data-testid={`nearby-radius-${r}`} onClick={() => pickRadius(r)}
            className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${radius === r ? "bg-[#FFC700] text-[#111317]" : "border border-white/15 text-slate-300 hover:border-white/40"}`}>{r} km</button>)}
        </div>
      </div>
      {!data && (
        <div className="mt-4">
          <p className="text-sm text-slate-400">Use a localização do celular para ver lojas de materiais de construção, ferragens e tintas por perto e comparar preços para o seu orçamento.</p>
          <button type="button" data-testid="nearby-locate-btn" className="btn-hazard mt-4" disabled={busy} onClick={() => locate()}>
            {busy ? <Loader2 className="animate-spin" size={18} /> : <LocateFixed size={18} />}{busy ? "Procurando lojas…" : "Usar minha localização"}
          </button>
        </div>
      )}
      {err && <p className="mt-3 text-sm text-[#FF5722]" data-testid="nearby-error">{err}</p>}
      {data && (
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400" data-testid="nearby-summary">
            {busy ? <Loader2 className="animate-spin" size={14} /> : null}
            <span>{data.stores.length} lojas em até {data.radius_km} km · {data.source === "gps" ? "pela sua localização" : "pelo endereço do cadastro"}</span>
            <button type="button" className="font-bold text-[#FFC700] hover:underline" onClick={() => locate()} data-testid="nearby-relocate">Atualizar localização</button>
          </div>
          {data.partners?.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="nearby-partners">
              {data.partners.map((s) => <a key={s.id} href={s.url || undefined} target="_blank" rel="noreferrer" className="rounded-xl border border-[#FFC700]/40 bg-[#FFC700]/10 px-3 py-2 text-sm"><b className="text-[#FFC700]">Parceira</b> · {s.name}{s.city && <span className="text-slate-400"> · {s.city}</span>}</a>)}
            </div>
          )}
          {data.stores.length > 0 && (
            <div className="map-dark h-64 overflow-hidden rounded-2xl border border-white/10" data-testid="nearby-map">
              <MapContainer key={`${data.center.join()}-${data.radius_km}`} center={data.center} zoom={data.radius_km <= 2 ? 14 : data.radius_km <= 5 ? 13 : 12} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <FitStores center={data.center} stores={data.stores} />
                <CircleMarker center={data.center} radius={9} pathOptions={{ color: "#3B82F6", fillColor: "#3B82F6", fillOpacity: 0.9 }}><Popup>Você</Popup></CircleMarker>
                {data.stores.map((s) => (
                  <CircleMarker key={`${s.id}-${s.lat}`} center={[s.lat, s.lng]} radius={8} pathOptions={{ color: "#111317", weight: 2, fillColor: "#FFC700", fillOpacity: 0.95 }}>
                    <Popup><b>{s.name}</b><br />{s.distance_km} km</Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
          )}
          {data.stores.length === 0 && <p className="text-sm text-slate-400" data-testid="nearby-empty">Nenhuma loja encontrada neste raio. Tente um raio maior.</p>}
          <div className="grid gap-3 sm:grid-cols-2">{list.map((s) => <StoreCard key={`${s.id}-${s.lat}`} s={s} />)}</div>
          <p className="text-xs text-slate-500">Dados de lojas: © colaboradores do OpenStreetMap. Confirme horário e estoque antes de ir.</p>
        </div>
      )}
    </div>
  );
}
